import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, IconButton, InputAdornment,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { axiosInstance } from '../../api';
import { useCompany } from '../../context/CompanyContext';

export default function AdminResetPassword({ adminId, companyCode, onComplete }) {
  const company = useCompany();
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post('api/admin/reset-password/', {
        admin_id: adminId,
        new_username: newUsername || undefined,
        new_password: newPassword,
      });
      onComplete();
    } catch (err) {
      setError(err.response?.data?.error || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
    }}>
      <Paper elevation={12} sx={{ p: 5, maxWidth: 450, width: '100%', borderRadius: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {!company.loading && company.logo && (
            <Box sx={{ mb: 2 }}>
              <img src={company.logo} alt={company.name || 'Company'} style={{ maxHeight: 60, maxWidth: 180, objectFit: 'contain' }} />
            </Box>
          )}
          {!company.loading && company.name && (
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
              {company.name}
            </Typography>
          )}
          <LockResetIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
            Reset Your Password
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
            Your account requires a password change before you can continue.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth label="New Username (optional)"
            value={newUsername} onChange={(e) => setNewUsername(e.target.value)}
            sx={{ mb: 2 }} helperText="Leave blank to keep your current username"
          />
          <TextField
            fullWidth label="New Password" type={showPassword ? 'text' : 'password'}
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }} required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth label="Confirm Password" type={showConfirm ? 'text' : 'password'}
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ mb: 3 }} required
            error={confirmPassword !== '' && newPassword !== confirmPassword}
            helperText={confirmPassword !== '' && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" size="small">
                    {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" variant="contained" fullWidth disabled={loading}
            sx={{ py: 1.5, fontWeight: 700, fontSize: '1rem', borderRadius: 2, bgcolor: '#f59e0b',
              '&:hover': { bgcolor: '#d97706' } }}>
            {loading ? 'Updating...' : 'Update Password'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
